/**
 * PATH: apps/quote/src/components/builder/panels/ClaimsPanel.tsx
 * PURPOSE: Claims panel for payment claim management
 */

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Receipt,
  Plus,
  ExternalLink,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { quoteApi, type QuoteDetail } from '@/lib/quoteApi'

interface ClaimsPanelProps {
  quoteId: string
  quote: QuoteDetail | null
}

const STATUS_CONFIG = {
  draft: { label: 'Draft', icon: Clock, className: 'text-slate-600 bg-slate-100' },
  submitted: { label: 'Submitted', icon: Clock, className: 'text-blue-600 bg-blue-100' },
  approved: { label: 'Approved', icon: CheckCircle, className: 'text-emerald-600 bg-emerald-100' },
  rejected: { label: 'Rejected', icon: AlertCircle, className: 'text-red-600 bg-red-100' },
  paid: { label: 'Paid', icon: DollarSign, className: 'text-green-600 bg-green-100' },
}

export default function ClaimsPanel({ quoteId, quote }: ClaimsPanelProps) {
  const navigate = useNavigate()

  const { data: claims = [], isLoading } = useQuery({
    queryKey: ['quoteClaims', quoteId],
    queryFn: () => quoteApi.getPaymentClaims(quoteId),
    staleTime: 30 * 1000,
  })

  const totalValue = quote?.total_chargeout || 0
  const claimedValue = claims.reduce((sum, c) => sum + (c.claim_amount || 0), 0)
  const progressPercent = totalValue > 0 ? (claimedValue / totalValue) * 100 : 0

  return (
    <div className="h-full flex flex-col">
      <div className="flex-shrink-0 p-3 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950 dark:to-teal-950">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Progress Claims</h3>
          <span className="text-xs font-medium text-emerald-600">{progressPercent.toFixed(1)}%</span>
        </div>
        <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500"
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>
        <div className="flex justify-between mt-1.5 text-xs text-slate-600 dark:text-slate-400">
          <span>Claimed: {formatCurrency(claimedValue)}</span>
          <span>Total: {formatCurrency(totalValue)}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : claims.length === 0 ? (
          <div className="text-center py-6 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
            <Receipt className="w-8 h-8 mx-auto text-slate-300 mb-1" />
            <p className="text-sm text-slate-500">No claims yet</p>
          </div>
        ) : (
          claims.map((claim) => {
            const status = STATUS_CONFIG[claim.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.draft
            const StatusIcon = status.icon
            return (
              <div
                key={claim.id}
                className="p-2.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer"
                onClick={() => navigate(`/claims/${claim.id}`)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">Claim #{claim.claim_number}</p>
                    <p className="text-xs text-slate-500">{new Date(claim.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className={cn('px-2 py-0.5 rounded-full flex items-center gap-1 text-xs font-medium', status.className)}>
                    <StatusIcon className="w-3 h-3" />
                    {status.label}
                  </div>
                </div>
                <div className="flex justify-between mt-2 pt-2 border-t border-slate-100">
                  <span className="text-xs text-slate-500">{claim.items_count || 0} items</span>
                  <span className="text-sm font-semibold text-emerald-600">{formatCurrency(claim.claim_amount || 0)}</span>
                </div>
              </div>
            )
          })
        )}
      </div>

      <div className="flex-shrink-0 p-3 border-t border-slate-200 dark:border-slate-800 space-y-2">
        <button
          onClick={() => navigate(`/claims/new?quoteId=${quoteId}`)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          New Claim
        </button>
        <button
          onClick={() => navigate('/claims')}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-600"
        >
          <ExternalLink className="w-4 h-4" />
          View All Claims
        </button>
      </div>
    </div>
  )
}

